import React, {forwardRef, ReactNode, useEffect, useRef, useState} from "react"
import {useTranslation} from "react-i18next"

import CircleIcon from "@mui/icons-material/Circle"
import CloseIcon from "@mui/icons-material/Close"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Typography from "@mui/material/Typography"

import i18next from "i18next"
import Moment from "moment"
import {closeSnackbar, enqueueSnackbar, OptionsObject, SnackbarContent} from "notistack"
import {v4 as uuidv4} from "uuid"

import {JackhammerIcon} from "src/components/common/NdIcons/JackhammerIcon"
import {LockIcon} from "src/components/common/NdIcons/LockIcon"
import {SendIcon} from "src/components/common/NdIcons/SendIcon"
import {WarningIcon} from "src/components/common/NdIcons/WarningIcon"
import {localStorageItemName} from "src/constants/local-storage-item-name"

/**
 * The types of toasts that can be displayed.
 *
 * @enum {string}
 * @property {string} SUCCESS - Represents a success toast.
 * @property {string} WARNING - Represents a warning toast.
 * @property {string} ERROR - Represents an error toast.
 * @property {string} INFO - Represents an informational toast.
 */
const enum NdToastType {
    SUCCESS = "success",
    WARNING = "warning",
    ERROR = "error",
    INFO = "info"
}

export interface SimpleToasterProps extends OptionsObject {
    id: number | string
    type: NdToastType | string
    title: string
    message: string
    code?: number
    createdAt: string
}

export type ToastInput = Omit<SimpleToasterProps, keyof OptionsObject>

/**
 * Shows a toast with the given properties.
 * This is a wrapper around `enqueueSnackbar` from notistack.
 *
 * @param {ToastInput} props - The properties of the toast to be displayed.
 */
export const showToast = (props: ToastInput) => {
    enqueueSnackbar(props as unknown as SimpleToasterProps)
}

/** The parts of a toast a caller may still want to set itself, all optional. */
export type ToastDetails = Partial<Omit<ToastInput, "type" | "message">>

/**
 * Stamp shown next to the toast title, in the language the user picked.
 */
export const toastDate = (): string =>
    Moment(new Date())
        .locale(localStorage.getItem(localStorageItemName.language) ?? "")
        .format("ll")

/**
 * Shows an error toast.
 *
 * @param {string} message - The error message to display.
 * @param {ToastDetails} [details] - Overrides, e.g. a specific `title` or an error `code`.
 */
export const showErrorToast = (message: string, details?: ToastDetails) => {
    showToast({
        type: NdToastType.ERROR,
        title: i18next.t("common:Error"),
        message,
        id: uuidv4(),
        createdAt: toastDate(),
        ...details
    })
}

/**
 * Shows a success toast.
 *
 * @param {string} message - The message to display.
 * @param {ToastDetails} [details] - Overrides, e.g. a specific `title` or a `code`.
 */
export const showSuccessToast = (message: string, details?: ToastDetails) => {
    showToast({
        type: NdToastType.SUCCESS,
        title: i18next.t("common:Success"),
        message,
        id: uuidv4(),
        createdAt: toastDate(),
        code: 200,
        ...details
    })
}

/**
 * Returns the color of the stroke for the toast based on its type.
 *
 * @param {SimpleToasterProps["type"]} type - The type of the toast (success, warning, error, info).
 * @returns {string} - The color of the stroke for the toast.
 */
const getToastStroke = (type: SimpleToasterProps["type"]): string => {
    switch (type) {
        case NdToastType.SUCCESS:
            return "#307ADE"
        case NdToastType.WARNING:
            return "#EB9939"
        case NdToastType.ERROR:
            return "#DA4FA3"
        case NdToastType.INFO:
        default:
            return "#9747FF"
    }
}

/**
 * Returns the appropriate icon component for the toast based on its type and color.
 *
 * @param {SimpleToasterProps["type"]} type - The type of the toast.
 * @param {string} color - The color of the icon.
 * @returns {ReactNode} - The rendered icon component.
 */
const getToastIcon = (type: SimpleToasterProps["type"], color: string): ReactNode => {
    switch (type) {
        case NdToastType.SUCCESS:
            return <SendIcon stroke={color} size={18} />
        case NdToastType.WARNING:
            return <WarningIcon stroke={color} size={18} />
        case NdToastType.ERROR:
            return <JackhammerIcon stroke={color} size={18} />
        case NdToastType.INFO:
        default:
            return <LockIcon stroke={color} size={18} />
    }
}

const mainInfoSx = {
    fontSize: "14px"
}

const subInfoSx = {
    fontSize: "12px"
}

/**
 * Custom toast component to be used with notistack.
 *
 * @param {SimpleToasterProps} props - The properties of the toast.
 * @param {React.Ref<HTMLDivElement>} ref - The reference to the toast component.
 * @returns {JSX.Element} - The rendered toast component.
 */
export const NdToast = forwardRef<HTMLDivElement, SimpleToasterProps>((props: SimpleToasterProps, ref) => {
    const {t} = useTranslation("common")
    const componentsColor = getToastStroke(props.type)
    const iconComponent = getToastIcon(props.type, componentsColor)

    const [isTruncated, setTruncated] = useState<boolean>(true)
    const [isOverflowing, setOverflowing] = useState<boolean>(false)

    const messageRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = messageRef.current

        if (el) {
            setOverflowing(el.scrollHeight > el.clientHeight)
        }
    }, [props.message])

    return (
        <SnackbarContent ref={ref}>
            <Card
                sx={{
                    backgroundColor: "white",
                    width: "400px",
                    "::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "4px",
                        height: "100%",
                        backgroundColor: componentsColor,
                        borderRadius: "15px 0px 0px 15px"
                    },
                    "& .MuiCardContent-root:last-child": {
                        paddingBottom: "0.4rem"
                    }
                }}
            >
                <CardContent sx={{padding: "0.4rem 0.8rem 0.4rem 0.8rem"}}>
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {iconComponent}
                            <Typography
                                display="inline"
                                sx={{
                                    ...mainInfoSx,
                                    fontWeight: "bold",
                                    marginLeft: "0.5rem"
                                }}
                            >
                                {props.title}
                            </Typography>
                            <CircleIcon
                                sx={{color: "gray", fontSize: "5px", margin: "0 8px", marginBottom: "0.1rem"}}
                            />
                            <Typography
                                display="inline"
                                sx={{
                                    ...subInfoSx,
                                    fontWeight: 300,
                                    color: "#393e46"
                                }}
                            >
                                {props.createdAt}
                            </Typography>
                            {props.code && (
                                <>
                                    <CircleIcon
                                        sx={{
                                            color: "gray",
                                            fontSize: "3px",
                                            margin: "0 8px",
                                            marginBottom: "0.1rem"
                                        }}
                                    />
                                    <Typography
                                        display="inline"
                                        sx={{
                                            fontWeight: 300,
                                            fontSize: "10px",
                                            color: "#393e46"
                                        }}
                                    >
                                        Code
                                    </Typography>
                                    &nbsp;
                                    <Typography
                                        display="inline"
                                        sx={{
                                            fontWeight: 300,
                                            fontSize: "10px",
                                            color: "black"
                                        }}
                                    >
                                        {props.code}
                                    </Typography>
                                </>
                            )}
                        </Box>
                        <IconButton aria-label="delete" onClick={() => closeSnackbar(props.id)}>
                            <CloseIcon sx={mainInfoSx} />
                        </IconButton>
                    </Box>
                    <Typography
                        sx={{
                            ...mainInfoSx,
                            color: "#393e46",
                            marginTop: "0.3rem",
                            ...(isTruncated && {
                                display: "-webkit-box",
                                overflow: "hidden",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2
                            })
                        }}
                    >
                        {props.message}
                    </Typography>

                    {isOverflowing && (
                        <Box sx={{display: "flex", justifyContent: "flex-end", marginTop: "1%"}}>
                            <Link
                                href="#"
                                onClick={() => setTruncated(!isTruncated)}
                                sx={{...subInfoSx, color: "black", opacity: "60%"}}
                            >
                                {isTruncated ? t("Expand") : t("Collapse")}
                            </Link>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </SnackbarContent>
    )
})
